import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import axios from 'axios';
import PageHeader from '../../../shared/page-header/PageHeader';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { getModulePermissions } from '../../../utils/permissionHelper';
import { logAuditEvent } from '../../../utils/auditLogger';
import { getStorageValues } from '../../../shared/CommonFunctions';
import ProductPromoBannerTab from '../product/ProductPromoBannerTab';
import ProductImagesTab from '../product/ProductImagesTab';
import { buildProductSavePayload, mapProductFromApiAsync } from '../product/utils/mapProduct';
import { validateProduct } from '../product/productValidation';

const hasPromoImage = (formData) =>
  (formData?.Images || []).some(
    (image) => String(image.ImageType || '').toLowerCase() === 'promo',
  );

function PromoBannerMain(props) {
  const [alert, setAlert] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [formData, setFormData] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userModulePermission, setUserModulePermission] = useState({});
  const moduleName = props.Module || 'Promo Banners';
  const canEdit = userModulePermission.EditModule === 1;

  const selectedOption = useMemo(
    () => productOptions.find((option) => option.value === selectedProductId) || null,
    [productOptions, selectedProductId],
  );

  const fetchProductOptions = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/ec-product/getAll`);
      const list = response.data?.data || [];
      setProductOptions(
        list.map((product) => ({
          value: product.ProductId,
          label: product.ProductName || `Product ${product.ProductId}`,
        })),
      );
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to load products!!', 'Error', setAlert);
    }
  };

  const loadProduct = async (productId) => {
    if (!productId) {
      setFormData(null);
      return;
    }
    setLoadingProduct(true);
    try {
      const response = await axios.get(`${getApiBaseUrl()}/ec-product/getById`, {
        params: { ProductId: productId },
      });
      if (response.data?.data) {
        const mapped = await mapProductFromApiAsync(response.data.data);
        setFormData(mapped);
      } else {
        setFormData(null);
        CreateAlert('Product not found!!', 'Warning', setAlert);
      }
    } catch (error) {
      console.error(error);
      setFormData(null);
      CreateAlert('Failed to load product!!', 'Error', setAlert);
    } finally {
      setLoadingProduct(false);
    }
  };

  useEffect(() => {
    const modulePermissions = getModulePermissions(props.Module);
    setUserModulePermission(modulePermissions || {});
    fetchProductOptions();
    logAuditEvent(moduleName, 'Accessed Module', 'Accessed Promo Banners module');
  }, [props.Module, moduleName]);

  const handleProductChange = (option) => {
    const productId = option ? Number(option.value) : null;
    setSelectedProductId(productId);
    loadProduct(productId);
  };

  const handleSave = async () => {
    if (!formData?.ProductId || isSubmitting || !canEdit) return;

    if (formData.IsPromoBanner && !hasPromoImage(formData)) {
      CreateAlert('A promo banner image is required before you can save with promo enabled', 'Warning', setAlert);
      return;
    }

    const payload = buildProductSavePayload(formData, getStorageValues('userId'));
    if (!validateProduct(payload, setAlert)) return;

    setIsSubmitting(true);
    logAuditEvent(moduleName, 'Save', `Saved promo banner for product ${formData.ProductName || formData.ProductId}`);
    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-product/save`, payload);
      const result = response.data?.data;
      if (result?.success === 1) {
        CreateAlert(result.message || 'Promo banner saved successfully!!', 'Success', setAlert);
        await loadProduct(formData.ProductId);
        await fetchProductOptions();
      } else {
        CreateAlert(result?.message || 'Failed to save promo banner!!', 'Error', setAlert);
      }
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to save promo banner!!', 'Error', setAlert);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SKAlerts alert={alert} />
      <PageHeader
        displayModule={props.Module}
        displayText="Manage "
        showAddButton={false}
        showRefresh={false}
        showPDF={false}
        showExcel={false}
        showPrint={false}
      />

      <div className="card mb-3">
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-lg-6">
              <label className="form-label">Product</label>
              <Select
                isClearable
                placeholder="Select a product"
                options={productOptions}
                value={selectedOption}
                onChange={handleProductChange}
              />
            </div>
            {formData && canEdit ? (
              <div className="col-lg-6 text-lg-end mt-3 mt-lg-0">
                <button
                  type="button"
                  className="btn btn-submit"
                  onClick={handleSave}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {!selectedProductId ? (
        <div className="alert alert-info">Select a product to manage its homepage promo banner.</div>
      ) : loadingProduct ? (
        <div className="alert alert-info">Loading product...</div>
      ) : formData ? (
        <>
          <ProductPromoBannerTab
            formData={formData}
            sendDataToParent={setFormData}
            readOnly={!canEdit}
          />
          <div className="mt-3">
            <h6 className="mb-2">Promo banner image</h6>
            <ProductImagesTab
              productId={formData.ProductId}
              images={formData.Images}
              isPromoBanner={!!formData.IsPromoBanner}
              imageMode="promo"
              readOnly={!canEdit}
              onRefresh={() => loadProduct(formData.ProductId)}
            />
          </div>
        </>
      ) : null}
    </>
  );
}

export default PromoBannerMain;
